import React from 'react';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SampleDetails} from 'constants/SampleConstants';
import {AddExternalSamplesModal} from 'notebook/sample-propagation/legacy/legacy-add-samples-modal/AddExternalSamplesModal';
import AddSamplesFromIdsModal from 'notebook/sample-propagation/legacy/legacy-add-samples-modal/AddSamplesFromIdsModal';
import {AddStandaloneSamplesModal} from 'notebook/sample-propagation/legacy/legacy-add-samples-modal/AddStandaloneSamplesModal';
import LegacySamplesSourceCollector from 'notebook/sample-propagation/legacy/LegacySampleSourceCollector';
import {SimpleSampleFilters} from 'notebook/sample-propagation/SampleFilter';
import {Note} from 'notebook/util/NoteUtilConstants';
import LegacyDropdownMenuItem from 'ui-library/dropdowns/LegacyDropdownMenuConstants';
import runModal from 'ui-library/runModal';
import fillFromWorklist from 'worklists/util/fillFromWorklist';
import validateWorklistItemsTransferable from 'worklists/validators/validateWorklistItemsTransferable';
import {WORKLIST_FILL_LOCATION, WorklistType} from 'worklists/WorklistConstants';

interface TransferTableStandaloneLinkContextProps {
  children: (args: {canAddSamples: boolean; addMenuItems: Array<LegacyDropdownMenuItem>}) => JSX.Element;
  entryId: string;

  onAddSampleRows: (sampleDetails: Array<SampleDetails>) => void;
  note: Note;
}

enum AddSamplesModalType {
  EXTERNAL = 'EXTERNAL',
  INTERNAL = 'INTERNAL',
  NONE = 'NONE',
}

interface TransferTableStageRunStandaloneContextState {
  modal: AddSamplesModalType;
}

export class TransferTableStandaloneLinkContext extends React.PureComponent<
  TransferTableStandaloneLinkContextProps,
  TransferTableStageRunStandaloneContextState
> {
  state: TransferTableStageRunStandaloneContextState = {modal: AddSamplesModalType.NONE};
  sampleFilter = [SimpleSampleFilters.BIOENTITY, SimpleSampleFilters.BATCH, SimpleSampleFilters.CONTAINER];

  _handleAddInternalSamplesClick = (): void => {
    this.setState({modal: AddSamplesModalType.INTERNAL});
  };

  _handleAddExternalSamplesClick = (): void => {
    this.setState({modal: AddSamplesModalType.EXTERNAL});
  };

  _handleAddFromWorklistClick = async (): Promise<void> => {
    const worklist = await fillFromWorklist(
      [WorklistType.CONTAINER, WorklistType.BIOENTITY, WorklistType.BATCH],
      WORKLIST_FILL_LOCATION.TRANSFER_TABLE,
      validateWorklistItemsTransferable
    );

    if (worklist) {
      runModal(AddSamplesFromIdsModal, {
        sourceMetadata: [
          {
            name: worklist.name,
            icon: ModelTypeConstants.WORKLIST,
            itemIds: worklist.itemIds,
          },
        ],
        title: 'Add samples from worklist',
        enableRowCounts: false,
        samplesHaveStageRuns: false,
        onSamplesSelected: this._handleAddSamples,
        noSamplesMessage: 'There are no samples in this worklist.',
      });
    }
  };

  _handleCloseModal = (): void => {
    this.setState({modal: AddSamplesModalType.NONE});
  };

  _canAddSamples(): boolean {
    // TODO(Jeff)
    return true;
  }

  _handleAddSamples = (samples: Array<SampleDetails>): void => {
    this.props.onAddSampleRows(samples);
    this._handleCloseModal();
  };

  _renderAddInternalSamplesModal(): JSX.Element {
    return (
      <LegacySamplesSourceCollector
        note={this.props.note}
        entryId={this.props.entryId}
        limitSamplesTo={this.sampleFilter}
      >
        {(sampleSources) => (
          <AddStandaloneSamplesModal
            onRequestClose={this._handleCloseModal}
            onSamplesSelected={this._handleAddSamples}
            sources={sampleSources}
          />
        )}
      </LegacySamplesSourceCollector>
    );
  }

  _renderAddExternalSamplesModal(): JSX.Element {
    return (
      <AddExternalSamplesModal
        onRequestClose={this._handleCloseModal}
        onSamplesSelected={this._handleAddSamples}
      />
    );
  }

  render(): JSX.Element {
    return (
      <>
        {this.state.modal === AddSamplesModalType.INTERNAL && this._renderAddInternalSamplesModal()}
        {this.state.modal === AddSamplesModalType.EXTERNAL && this._renderAddExternalSamplesModal()}
        {this.props.children({
          canAddSamples: this._canAddSamples(),
          addMenuItems: _.compact([
            {
              name: 'Add row',
              icon: 'compose',
              handleOnClick: this._handleAddExternalSamplesClick,
            },

            {
              name: 'Add samples',
              icon: 'compose',
              handleOnClick: this._handleAddInternalSamplesClick,
            },
            BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS
              ? {
                  name: 'Fill from worklist',
                  icon: ModelTypeConstants.WORKLIST,
                  handleOnClick: this._handleAddFromWorklistClick,
                }
              : null,
          ]),
        })}
      </>
    );
  }
}

export default TransferTableStandaloneLinkContext;
